import { Loader } from "@obs/base";
import { Locale } from "@obs/providers";
import { createFileRoute, Navigate, redirect } from "@tanstack/react-router";
import { z } from "zod";

import { TripTrackingPage } from "../../components/TripTrackingPage";
import { useGetService } from "../../Hooks/useGetService";
import { SUPPORTED_LOCALES } from "../../intl/constants";
import { normalizeLocale } from "../../intl/normalizeLocale";

export const tripTrackingSearchSchema = z.object({
	// origin
	o: z
		.string()
		.length(3)
		.optional()
		.catch((ctx) => {
			console.error("Invalid origin code:", ctx.value);

			return undefined;
		}),
	// destination
	d: z
		.string()
		.length(3)
		.optional()
		.catch((ctx) => {
			console.error("Invalid destination code:", ctx.value);

			return undefined;
		}),
	locale: z
		.enum(SUPPORTED_LOCALES)
		.optional()
		.catch((ctx) => {
			console.error("Invalid locale:", ctx.value);

			return undefined;
		}),
});

export const Route = createFileRoute("/$scheduledDate/$serviceName")({
	component: RouteComponent,
	validateSearch: (search) =>
		tripTrackingSearchSchema.parse({
			...search,
			locale: search.locale && typeof search.locale === "string" ? normalizeLocale(search.locale) : undefined,
		}),
	beforeLoad: ({ params, search }) => {
		// Check if serviceName matches date format YYYY-MM-DD
		const datePattern = /^\d{4}-\d{2}-\d{2}$/;
		if (datePattern.test(params.serviceName)) {
			throw redirect({
				to: "/$scheduledDate/$serviceName",
				params: {
					serviceName: params.scheduledDate,
					scheduledDate: params.serviceName,
				},
				search,
			});
		}
	},
});

function RouteComponent() {
	const locale = Locale.useCurrentLocale();
	const { serviceName, scheduledDate } = Route.useParams();
	const { o, d } = Route.useSearch();

	const { data, isLoading, isError, error } = useGetService({
		service_name: serviceName,
		scheduled_date: scheduledDate,
		"X-Locale": locale,
		refetchInterval: 60_000, // refetch every minute
	});

	if (isLoading) {
		return <Loader.State />;
	}

	if (isError && error instanceof Response && error.status !== 404) {
		throw error;
	}

	if (isError || !data) {
		return <Navigate to={`/404`} href={`/404?locale=${locale}`} replace />;
	}

	return <TripTrackingPage service={data} origin={o} destination={d} />;
}
